import type {
  AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig,
} from 'axios';
import axios from 'axios';
import { formatISO } from 'date-fns';
import QueryString from 'qs';

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (!value || typeof value !== 'object') return false;

  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
};

// Ни один из наших API не принимает undefined в теле запроса:
// рекурсивно вычищаем такие поля только из JSON-совместимых объектов,
// чтобы не преобразовать File, Blob, FormData и другие специальные типы в обычный объект.
const removeUndefined = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(removeUndefined);
  if (isPlainObject(value)) {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([, item]) => item !== undefined)
        .map(([key, item]) => [key, removeUndefined(item)]),
    );
  }
  return value;
};

// Схлопывает двойные слэши в собранном URL, сохраняя scheme `://`.
// База `/` + путь `/6/...` → `/6/...`, а не protocol-relative `//6/...`.
export const normalizeUrl = (url: string): string => url.replace(/(?<!:)\/\/+/g, '/');

export const createAxiosInstance = (props: AxiosRequestConfig | undefined): AxiosInstance => {
  const instance = axios.create({
    // baseURL: 'https://example.com',
    // headers, timeout, etc.
    paramsSerializer: (params) => QueryString.stringify(params, {
      arrayFormat: 'comma',
      serializeDate: formatISO,
    }),
    ...props,
  });

  instance.interceptors.request.use((config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    if (config.data && typeof config.data === 'object') {
      return { ...config, data: removeUndefined(config.data) };
    }
    return config;
  });

  return instance;
};
